import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../api";
import { useAuth } from "../platform";

export type EventCard = {
  slug: string | null;
  link: string;
  name: string | null;
  description: string | null;
  date: string;
  time: string;
  endTime: string | null;
  types: string[];
  posterLabel: string;
  coverImageUrl: string | null;
  host: {
    firstName: string;
    lastName: string | null;
    group: { slug: string; name: string; imageUrl: string | null } | null;
  } | null;
  venue: { name: string | null; neighborhood: string | null; city: string | null };
  capacity: number | null;
  priceCents: number | null;
  attendeeCount: number;
  interestedCount: number;
  isPast: boolean;
  isRsvped: boolean;
  isInterested: boolean;
  isOutclose: boolean;
  isRecurring: boolean;
};

// Nearest events (by embedding distance) in the same city as a given event,
// split into genuinely-different `similar` events and `moreDates` — other dates
// of the same event (same name + venue). Empty when the source isn't embedded.
// "Other dates" only needs the date + link; it's a compact date-first UI.
export type OtherDate = { slug: string | null; link: string; date: string; time: string };

export type SimilarEventsResult = {
  similar: EventCard[];
  moreDates: OtherDate[];
};

export function useSimilarEvents(slug: string | null) {
  return useQuery({
    queryKey: ["similar-events", slug],
    queryFn: () => api.get<SimilarEventsResult>(`/events/public/${slug}/similar`),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// The signed-in user's liked (interested) upcoming events, soonest first.
export function useMyLikedEvents() {
  const { isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["my-liked-events"],
    queryFn: () => api.get<EventCard[]>(`/events/public/me/interested`),
    enabled: isSignedIn,
  });
}

export type EventCarousel = {
  id: string;
  title: string;
  largeCards: boolean;
  displayStyle?: "hero" | "list";
  layout?: "carousel" | "grid";
  items: EventCard[];
};

export type ActivityInfo = {
  name: string;
  emoji?: string;
  date?: string;
  time?: string;
  location?: string;
};

export type KikiParticipant = { id: string; firstName: string; lastInitial: string | null; profileImageUrl: string | null };

export type OpenKiki = {
  id: string;
  user: KikiParticipant;
  // Confirmed group: poster + accepted applicants.
  participants: KikiParticipant[];
  activity: string | null;
  activityInfo: ActivityInfo | null;
  note: string | null;
  preferences: Record<string, unknown> | null;
  createdAt: string;
  applicationCount: number;
  // null for standalone kikis
  event: {
    slug: string | null;
    name: string;
    date: string;
    time: string;
    coverImageUrl: string | null;
    types: string[];
    venueName: string | null;
    venueNeighborhood: string | null;
    venueCity: string | null;
  } | null;
};

export type EventsListResponse = {
  carousels: EventCarousel[];
  availablePresets: string[];
  city: { id: string; name: string; emoji: string } | null;
  openKikis: OpenKiki[];
};

export type TrendingCard = EventCard & { recentInterested: number };

const ALL_EVENTS_PAGE_SIZE = 24;

export type AllEventsPage = {
  items: EventCard[];
  total: number;
  offset: number;
  limit: number;
};

export function useAllEvents(opts?: {
  types?: string[];
  city?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}) {
  const types = opts?.types ?? [];
  const city = opts?.city ?? null;
  const dateFrom = opts?.dateFrom ?? null;
  const dateTo = opts?.dateTo ?? null;

  return useInfiniteQuery({
    queryKey: ["allEvents", { types, city, dateFrom, dateTo }],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("offset", String(pageParam));
      params.set("limit", String(ALL_EVENTS_PAGE_SIZE));
      if (types.length) params.set("types", types.join(","));
      if (city) params.set("city", city);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      return api.get<AllEventsPage>(`/events/public/all?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.success || !lastPage.data) return undefined;
      const { offset, limit, total } = lastPage.data;
      const next = offset + limit;
      return next < total ? next : undefined;
    },
  });
}

export function useTrending(limit?: number) {
  return useQuery({
    queryKey: ["trending", limit],
    queryFn: async () => {
      const qs = limit ? `?limit=${limit}` : "";
      return api.get<TrendingCard[]>(`/events/public/trending${qs}`);
    },
  });
}

export function useEventsList(opts?: {
  past?: boolean;
  types?: string[];
  city?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  mood?: string | null;
}) {
  const past = opts?.past ?? false;
  const types = opts?.types ?? [];
  const city = opts?.city ?? null;
  const dateFrom = opts?.dateFrom ?? null;
  const dateTo = opts?.dateTo ?? null;
  const mood = opts?.mood ?? null;
  return useQuery({
    queryKey: ["eventsList", { past, types, city, dateFrom, dateTo, mood }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (past) params.set("past", "true");
      if (types.length) params.set("types", types.join(","));
      if (city) params.set("city", city);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (mood) params.set("mood", mood);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return api.get<EventsListResponse>(`/events/public${qs}`);
    },
  });
}
